/**
 * Chat Manager Page
 * Admin dashboard for managing socket server media files
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, CardHeader, CardBody,
    Row, Col, Table, Button, Badge,
    Progress, Collapse, Modal, ModalHeader, ModalBody,
    FormGroup, Label, Input, Spinner, Alert
} from 'reactstrap';
import Swal from 'sweetalert2';
import chatManagerService from 'services/chatManager.service';

function ChatManager() {
    // Server status state
    const [serverStatus, setServerStatus] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);

    // Media stats state
    const [mediaStats, setMediaStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(true);

    // Settings state
    const [settings, setSettings] = useState({ autoDelete: { enabled: false, days: 30 } });
    const [savingSettings, setSavingSettings] = useState(false);

    // Expanded dates (for collapse)
    const [expandedDates, setExpandedDates] = useState({});

    // Media files by date
    const [mediaByDate, setMediaByDate] = useState({});
    const [loadingMedia, setLoadingMedia] = useState({});

    // Lightbox state
    const [lightbox, setLightbox] = useState(null);

    // Auto-delete day options
    const dayOptions = [5, 10, 15, 20, 25, 30, 90, 180];

    // Load initial data
    useEffect(() => {
        loadServerStatus();
        loadMediaStats();
        loadSettings();
    }, []);

    const loadServerStatus = async () => {
        try {
            setLoadingStatus(true);
            const data = await chatManagerService.getServerStatus();
            setServerStatus(data);
        } catch (err) {
            console.error('Load server status error:', err);
        } finally {
            setLoadingStatus(false);
        }
    };

    const loadMediaStats = async () => {
        try {
            setLoadingStats(true);
            const data = await chatManagerService.getMediaStats();
            setMediaStats(data);
        } catch (err) {
            console.error('Load media stats error:', err);
        } finally {
            setLoadingStats(false);
        }
    };

    const loadSettings = async () => {
        try {
            const data = await chatManagerService.getSettings();
            setSettings(data);
        } catch (err) {
            console.error('Load settings error:', err);
        }
    };

    const saveSettings = async (newSettings) => {
        try {
            setSavingSettings(true);
            await chatManagerService.updateSettings(newSettings.autoDelete);
            setSettings(newSettings);
            Swal.fire({ icon: 'success', title: 'Đã lưu cài đặt!', timer: 1500, showConfirmButton: false });
        } catch (err) {
            console.error('Save settings error:', err);
            Swal.fire({ icon: 'error', title: 'Lỗi', text: err.message });
        } finally {
            setSavingSettings(false);
        }
    };

    const toggleDateExpand = async (date) => {
        const newExpanded = { ...expandedDates, [date]: !expandedDates[date] };
        setExpandedDates(newExpanded);

        // Load media for date if expanding and not loaded
        if (newExpanded[date] && !mediaByDate[date]) {
            try {
                setLoadingMedia(prev => ({ ...prev, [date]: true }));
                const data = await chatManagerService.getMediaByDate(date);
                setMediaByDate(prev => ({ ...prev, [date]: data.files }));
            } catch (err) {
                console.error('Load media by date error:', err);
            } finally {
                setLoadingMedia(prev => ({ ...prev, [date]: false }));
            }
        }
    };

    const handleDeleteByDate = async (date) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Xác nhận xóa',
            text: `Bạn có chắc muốn xóa tất cả media của ngày ${formatDate(date)}?`,
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        });

        if (result.isConfirmed) {
            try {
                const response = await chatManagerService.deleteMediaByDates([date]);
                Swal.fire({ icon: 'success', title: `Đã xóa ${response.deleted} files!`, timer: 1500, showConfirmButton: false });
                loadMediaStats(); // Refresh stats
                setMediaByDate(prev => ({ ...prev, [date]: [] }));
            } catch (err) {
                Swal.fire({ icon: 'error', title: 'Lỗi', text: err.message });
            }
        }
    };

    // Format helpers
    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatSize = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const formatUptime = (seconds) => {
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${days}d ${hours}h ${minutes}m`;
    };

    const getProgressColor = (percent) => {
        if (percent >= 90) return 'danger';
        if (percent >= 70) return 'warning';
        return 'success';
    };

    // Render media preview based on type
    const renderMediaItem = (file) => {
        const url = chatManagerService.getMediaUrl(file.url);

        if (file.type === 'image') {
            return (
                <div
                    className="media-thumbnail"
                    style={{
                        width: '80px',
                        height: '80px',
                        backgroundImage: `url(${url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: '8px',
                        cursor: 'pointer'
                    }}
                    onClick={() => setLightbox({ url, type: 'image' })}
                />
            );
        }

        if (file.type === 'video') {
            // Check for thumbnail
            const thumbUrl = file.filename.includes('_thumb.')
                ? url
                : chatManagerService.getMediaUrl(file.url.replace(/\.[^.]+$/, '_thumb.jpg'));
            return (
                <div
                    style={{
                        width: '80px',
                        height: '80px',
                        backgroundImage: `url(${thumbUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        position: 'relative'
                    }}
                    onClick={() => setLightbox({ url, type: 'video' })}
                >
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(0,0,0,0.6)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px'
                    }}>
                        ▶
                    </div>
                </div>
            );
        }

        // Other files
        return (
            <div style={{
                width: '80px',
                height: '80px',
                background: '#f0f0f0',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                padding: '5px'
            }}>
                <i className={`fas ${file.type === 'pdf' ? 'fa-file-pdf' : 'fa-file'}`} style={{ fontSize: '24px', color: '#666' }} />
                <small style={{ fontSize: '10px', marginTop: '4px', textAlign: 'center', wordBreak: 'break-all' }}>
                    {file.filename.slice(-10)}
                </small>
            </div>
        );
    };

    // Shared card styles
    const cardStyle = {
        borderRadius: '20px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: 'none',
        overflow: 'hidden',
        marginBottom: '20px'
    };

    const headerStyle = (gradient) => ({
        background: gradient,
        padding: '20px 24px',
        borderBottom: 'none',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    });

    const headerLeft = (icon, title, subtitle) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <i className={`fas ${icon}`} style={{ fontSize: '32px', color: '#fff', opacity: 0.9 }}></i>
            <div style={{ lineHeight: '1.3' }}>
                <Label style={{ fontSize: '16px', color: '#fff', fontWeight: '700', marginBottom: '0', display: 'block' }}>
                    {title}
                </Label>
                {subtitle && (
                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                        {subtitle}
                    </span>
                )}
            </div>
        </div>
    );

    return (
        <div className="content">
            <style>{`
                .setting-switch {
                    position: relative;
                    display: inline-block;
                    width: 50px;
                    height: 26px;
                    flex-shrink: 0;
                }
                .setting-switch input {
                    opacity: 0;
                    width: 0;
                    height: 0;
                }
                .setting-switch .slider {
                    position: absolute;
                    cursor: pointer;
                    inset: 0;
                    background: #ccc;
                    border-radius: 26px;
                    transition: 0.3s;
                }
                .setting-switch .slider:before {
                    content: "";
                    position: absolute;
                    height: 20px;
                    width: 20px;
                    left: 3px;
                    bottom: 3px;
                    background: #fff;
                    border-radius: 50%;
                    transition: 0.3s;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
                }
                .setting-switch input:checked + .slider {
                    background: #51cbce;
                }
                .setting-switch input:checked + .slider:before {
                    transform: translateX(24px);
                }
                .setting-switch input:disabled + .slider {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
            `}</style>

            {/* Server Status Card */}
            <Row>
                <Col md="12">
                    <Card style={cardStyle}>
                        <CardHeader style={headerStyle('linear-gradient(135deg, #3498db 0%, #2c3e50 100%)')}>
                            {headerLeft('fa-globe', 'Server Status', serverStatus ? `${serverStatus.hostname} • ${serverStatus.ip}` : 'Đang kiểm tra...')}
                            {serverStatus && (
                                <Badge style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    fontSize: '12px',
                                    padding: '6px 14px',
                                    borderRadius: '20px'
                                }}>
                                    Uptime: {formatUptime(serverStatus.uptime)}
                                </Badge>
                            )}
                        </CardHeader>
                        <CardBody style={{ padding: '20px 24px' }}>
                            {loadingStatus ? (
                                <div className="text-center py-4">
                                    <Spinner color="primary" />
                                </div>
                            ) : serverStatus ? (
                                <Row>
                                    <Col md="6">
                                        <div style={{
                                            padding: '12px 16px',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '20px',
                                            marginBottom: '12px'
                                        }}>
                                            <div style={{ fontSize: '13px', color: '#8898aa', marginBottom: '4px' }}>CPU</div>
                                            <div style={{ fontSize: '15px', fontWeight: '600', color: '#2c3e50' }}>
                                                {serverStatus.cpu?.cores} cores — {serverStatus.cpu?.model?.slice(0, 40)}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md="3">
                                        <div style={{
                                            padding: '12px 16px',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '20px',
                                            marginBottom: '12px'
                                        }}>
                                            <div className="d-flex justify-content-between mb-1">
                                                <span style={{ fontSize: '13px', color: '#8898aa' }}>RAM</span>
                                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>
                                                    {serverStatus.memory?.percent}%
                                                </span>
                                            </div>
                                            <Progress value={serverStatus.memory?.percent} color={getProgressColor(serverStatus.memory?.percent)} style={{ height: '6px', borderRadius: '3px' }} />
                                            <div style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>
                                                {formatSize(serverStatus.memory?.used * 1024 * 1024)} / {formatSize(serverStatus.memory?.total * 1024 * 1024)}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md="3">
                                        <div style={{
                                            padding: '12px 16px',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '20px',
                                            marginBottom: '12px'
                                        }}>
                                            <div className="d-flex justify-content-between mb-1">
                                                <span style={{ fontSize: '13px', color: '#8898aa' }}>Disk</span>
                                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>
                                                    {serverStatus.disk?.percent}%
                                                </span>
                                            </div>
                                            <Progress value={serverStatus.disk?.percent} color={getProgressColor(serverStatus.disk?.percent)} style={{ height: '6px', borderRadius: '3px' }} />
                                            <div style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>
                                                {serverStatus.disk?.used} GB / {serverStatus.disk?.total} GB
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            ) : (
                                <Alert color="danger" style={{ borderRadius: '20px', margin: 0 }}>Không thể kết nối đến server</Alert>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Auto-Delete Settings */}
            <Row>
                <Col md="12">
                    <Card style={cardStyle}>
                        <CardHeader style={headerStyle('linear-gradient(135deg, #a044ff 0%, #6a3093 100%)')}>
                            {headerLeft('fa-cog', 'Auto-Delete Settings', 'Chạy tự động lúc 3:00 AM mỗi ngày')}
                            {savingSettings && <Spinner size="sm" style={{ color: '#fff' }} />}
                        </CardHeader>
                        <CardBody style={{ padding: '20px 24px' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 16px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '20px',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <i className="fas fa-trash-alt" style={{
                                        fontSize: '22px',
                                        color: settings.autoDelete?.enabled ? '#51cbce' : '#aaa'
                                    }}></i>
                                    <div>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>
                                            Tự động xóa files cũ
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#8898aa', marginTop: '2px' }}>
                                            {settings.autoDelete?.enabled
                                                ? `Xóa files cũ hơn ${settings.autoDelete?.days || 30} ngày`
                                                : 'Đang tắt — Không tự động xóa'}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    {settings.autoDelete?.enabled && (
                                        <Input
                                            type="select"
                                            value={settings.autoDelete?.days || 30}
                                            onChange={(e) => {
                                                const newSettings = {
                                                    ...settings,
                                                    autoDelete: { ...settings.autoDelete, days: parseInt(e.target.value) }
                                                };
                                                setSettings(newSettings);
                                                saveSettings(newSettings);
                                            }}
                                            style={{ width: '110px', borderRadius: '20px', fontSize: '13px' }}
                                            disabled={savingSettings}
                                        >
                                            {dayOptions.map(d => (
                                                <option key={d} value={d}>{d} ngày</option>
                                            ))}
                                        </Input>
                                    )}
                                    <label className="setting-switch" style={{ marginBottom: 0 }}>
                                        <input
                                            type="checkbox"
                                            checked={settings.autoDelete?.enabled || false}
                                            disabled={savingSettings}
                                            onChange={() => {
                                                const newSettings = {
                                                    ...settings,
                                                    autoDelete: { ...settings.autoDelete, enabled: !settings.autoDelete?.enabled }
                                                };
                                                setSettings(newSettings);
                                                saveSettings(newSettings);
                                            }}
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Media Storage */}
            <Row>
                <Col md="12">
                    <Card style={cardStyle}>
                        <CardHeader style={headerStyle('linear-gradient(135deg, #198754 0%, #0f5132 100%)')}>
                            {headerLeft('fa-images', 'Media Storage', mediaStats ? `${mediaStats.totalFiles} files • ${formatSize(mediaStats.totalSize)}` : 'Đang tải...')}
                            <Button
                                size="sm"
                                onClick={loadMediaStats}
                                style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    border: 'none',
                                    color: '#fff',
                                    borderRadius: '20px',
                                    padding: '6px 14px',
                                    fontSize: '12px'
                                }}
                            >
                                <i className="fas fa-sync-alt" style={{ marginRight: '4px' }}></i> Refresh
                            </Button>
                        </CardHeader>
                        <CardBody style={{ padding: '20px 24px', overflowY: 'auto', overflowX: 'hidden' }}>
                            {loadingStats ? (
                                <div className="text-center py-4">
                                    <Spinner color="primary" />
                                </div>
                            ) : mediaStats?.byDate?.length > 0 ? (
                                <Table style={{ marginBottom: 0 }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                                            <th style={{ width: '40px', border: 'none' }}></th>
                                            <th style={{ border: 'none', fontSize: '13px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase' }}>Ngày</th>
                                            <th style={{ border: 'none', fontSize: '13px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase' }}>Files</th>
                                            <th style={{ border: 'none', fontSize: '13px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase' }}>Dung lượng</th>
                                            <th style={{ width: '120px', border: 'none', fontSize: '13px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase' }}>Thao tác</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mediaStats.byDate.map(item => (
                                            <React.Fragment key={item.date}>
                                                <tr style={{ borderBottom: '1px solid #f5f5f5' }}>
                                                    <td style={{ border: 'none', verticalAlign: 'middle' }}>
                                                        <Button
                                                            color="link"
                                                            size="sm"
                                                            onClick={() => toggleDateExpand(item.date)}
                                                            style={{ color: '#2c3e50', padding: '2px 6px' }}
                                                        >
                                                            <i className={`fas fa-chevron-${expandedDates[item.date] ? 'down' : 'right'}`} />
                                                        </Button>
                                                    </td>
                                                    <td style={{ border: 'none', fontSize: '14px', fontWeight: '500', color: '#2c3e50', verticalAlign: 'middle' }}>{formatDate(item.date)}</td>
                                                    <td style={{ border: 'none', fontSize: '14px', color: '#525f7f', verticalAlign: 'middle' }}>{item.files}</td>
                                                    <td style={{ border: 'none', fontSize: '14px', color: '#525f7f', verticalAlign: 'middle' }}>{formatSize(item.size)}</td>
                                                    <td style={{ border: 'none', verticalAlign: 'middle' }}>
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            onClick={() => handleDeleteByDate(item.date)}
                                                            style={{ borderRadius: '20px', fontSize: '12px', padding: '4px 12px' }}
                                                        >
                                                            <i className="fas fa-trash" style={{ marginRight: '4px' }} /> Xóa
                                                        </Button>
                                                    </td>
                                                </tr>
                                                {/* Expanded media grid */}
                                                {expandedDates[item.date] && (
                                                    <tr>
                                                        <td colSpan="5" style={{ padding: '15px', background: '#fafbfc', border: 'none', borderRadius: '8px' }}>
                                                            {loadingMedia[item.date] ? (
                                                                <div className="text-center">
                                                                    <Spinner size="sm" color="primary" />
                                                                </div>
                                                            ) : mediaByDate[item.date]?.length > 0 ? (
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                                    {mediaByDate[item.date].map((file, idx) => (
                                                                        <div key={idx} title={`${file.filename} (${formatSize(file.size)})`}>
                                                                            {renderMediaItem(file)}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <p className="text-muted mb-0">Không có file</p>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <p className="text-muted text-center py-4" style={{ marginBottom: 0 }}>Không có media files</p>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Lightbox Modal */}
            <Modal isOpen={!!lightbox} toggle={() => setLightbox(null)} size="lg" centered>
                <ModalHeader toggle={() => setLightbox(null)}>Preview</ModalHeader>
                <ModalBody className="text-center" style={{ background: '#000' }}>
                    {lightbox?.type === 'image' && (
                        <img
                            src={lightbox.url}
                            alt=""
                            style={{ maxWidth: '100%', maxHeight: '70vh' }}
                        />
                    )}
                    {lightbox?.type === 'video' && (
                        <video
                            src={lightbox.url}
                            controls
                            autoPlay
                            style={{ maxWidth: '100%', maxHeight: '70vh' }}
                        />
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
}

export default ChatManager;
